import React, { useState /*, useEffect */ } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import { ArrowBack, Phone } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { formatPhone } from '../../utils/phoneFormatter';

interface BusinessLoginFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const BusinessLoginForm: React.FC<BusinessLoginFormProps> = ({ onBack, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { loginBusiness, clearError, state } = useAuth();
  const { isMobile } = useResponsive();

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setLoading(true);
    setError('');
    clearError();

    try {
      const result = await loginBusiness({ phone });
      
      if (result.error) {
        setError(result.error);
        setLoading(false);
      }
      // Если нет ошибки, loginBusiness уже перенаправил на MTS ID
    } catch (err: any) {
      setError(err.message || 'Ошибка получения ссылки авторизации');
      setLoading(false);
    }
  };


  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    // Очищаем ошибки при изменении номера
    if (error) setError('');
    if (state.error) clearError();
  };

  const isPhoneValid = phone.replace(/\D/g, '').length === 11;

  return (
    <Box>
      {/* Заголовок с кнопкой назад */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onBack}
          sx={{ mr: 1 }}
        >
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Вход для бизнеса
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Введите номер телефона для входа через MTS ID
          </Typography>
        </Box>
      </Box>

      {/* Ошибка */}
      {(error || state.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || state.error}
        </Alert>
      )}

      {/* Форма ввода телефона */}
      <form onSubmit={handlePhoneSubmit}>
        <TextField
          fullWidth
          label="Номер телефона"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="+7 (999) 999-99-99"
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          helperText="Номер телефона, привязанный к MTS ID"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={!isPhoneValid || loading}
          sx={{
            height: isMobile ? 48 : 56,
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: 'bold',
            backgroundColor: '#E31E24', // Цвета MTS
            '&:hover': {
              backgroundColor: '#C71E1E',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Войти через MTS ID'
          )}
        </Button>
      </form>

      {/* Информация о MTS ID */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          <strong>MTS ID</strong> — единая система авторизации МТС для доступа к сервисам. 
          Используется для входа менеджеров, бизнес-заказчиков и образовательных учреждений.
        </Typography>
      </Box>
    </Box>
  );
};
